import { useMemo } from 'react';

import {
  Box,
  Divider,
  MetricsBox,
  Skeleton,
  Stack,
  StatusChipV2,
  tokens,
} from '@cast/design-system';
import { dashesOrNum, toBig } from '@cast/utils';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { CardCurrentDate } from '../_components/CardCurrentDate';
import {
  ReclaimedResourceItem,
  useReclaimedResourcesData,
} from './_hooks/useReclaimedResourcesData';

const CARD_TITLE = 'Reclaimed resources';

const BAR_HEIGHT = 58;

type BarKey = 'originalReq' | 'req' | 'rec' | 'usage';

const BAR_COLORS: Record<BarKey, string> = {
  originalReq: tokens.theme.data.grey200,
  req: tokens.theme.data.blue200,
  rec: tokens.theme.data.grey900,
  usage: tokens.theme.data.green200,
};

const BAR_KEYS: BarKey[] = ['originalReq', 'req', 'rec', 'usage'];

const ScaleChip = ({ diff }: { diff: number | undefined }) => {
  if (diff === undefined) {
    return null;
  }

  const isDownscaling = diff >= 0;

  return (
    <StatusChipV2
      label={isDownscaling ? 'Downscale' : 'Upscale'}
      color={isDownscaling ? 'success' : 'neutral'}
      variant="subtle"
      size="small"
    />
  );
};

const ResourceBars = ({
  data,
}: {
  data: ReclaimedResourceItem | undefined;
}) => {
  const heights = useMemo(() => {
    if (!data) {
      return { originalReq: 0, req: 0, rec: 0, usage: 0 };
    }

    const maxValue = Math.max(
      data.originalReq,
      data.req,
      data.rec,
      data.usage,
      0
    );

    const getHeight = (value: number) =>
      toBig(value)
        .div(maxValue || 1)
        .mul(BAR_HEIGHT)
        .toNumber();

    return {
      originalReq: getHeight(data.originalReq),
      req: getHeight(data.req),
      rec: getHeight(data.rec),
      usage: getHeight(data.usage),
    };
  }, [data]);

  return (
    <Stack
      direction="row"
      alignItems="flex-end"
      sx={{ height: BAR_HEIGHT, flexShrink: 0, gap: '1px' }}
    >
      {BAR_KEYS.map((key) => (
        <Box
          key={key}
          sx={{
            width: 16,
            height: heights[key],
            backgroundColor: BAR_COLORS[key],
            borderRadius: tokens.theme.radius.xs,
          }}
        />
      ))}
    </Stack>
  );
};

const ResourceRow = ({
  data,
  unit,
  testId,
}: {
  data: ReclaimedResourceItem | undefined;
  unit: string;
  testId: string;
}) => (
  <Stack direction="row" gap={24} alignItems="flex-start">
    <ResourceBars data={data} />
    <MetricsBox
      testId={testId}
      size="m"
      slots={{
        value: dashesOrNum({
          value: data ? Math.abs(data.diff) : data,
          roundBy: 2,
        }),
        unit,
        footer: <ScaleChip diff={data?.diff} />,
      }}
      slotProps={{
        value: { variant: 'metricmd' },
        unit: { variant: 'P12R' },
      }}
    />
  </Stack>
);

const ReclaimedResourcesCardSkeleton = () => (
  <Stack height="100%" gap={24} justifyContent="space-between">
    <Stack gap={24} sx={{ flexShrink: 0 }}>
      <Stack direction="row" gap={24} alignItems="flex-start">
        <Stack
          direction="row"
          alignItems="flex-end"
          sx={{ height: BAR_HEIGHT, flexShrink: 0, gap: '1px' }}
        >
          {BAR_KEYS.map((key) => (
            <Skeleton
              key={key}
              variant="rectangular"
              sx={{
                width: 16,
                height: BAR_HEIGHT,
                borderRadius: tokens.theme.radius.xs,
              }}
            />
          ))}
        </Stack>
        <Stack gap={4}>
          <Skeleton variant="text" width={80} />
          <Skeleton variant="text" width={40} />
        </Stack>
      </Stack>
      <Divider />
    </Stack>
    <Stack direction="row" gap={24} alignItems="flex-start">
      <Stack
        direction="row"
        alignItems="flex-end"
        sx={{ height: BAR_HEIGHT, flexShrink: 0, gap: '1px' }}
      >
        {BAR_KEYS.map((key) => (
          <Skeleton
            key={key}
            variant="rectangular"
            sx={{
              width: 16,
              height: BAR_HEIGHT,
              borderRadius: tokens.theme.radius.xs,
            }}
          />
        ))}
      </Stack>
      <Stack gap={4}>
        <Skeleton variant="text" width={80} />
        <Skeleton variant="text" width={40} />
      </Stack>
    </Stack>
  </Stack>
);

export const ReclaimedResourcesCard = () => {
  const { data, isLoading, isFetching, error, refetch } =
    useReclaimedResourcesData();

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  return (
    <WidgetLayout
      title={CARD_TITLE}
      testId="reclaimed-resources-card"
      headerEndContent={<CardCurrentDate />}
    >
      {isCardLoading ? (
        <ReclaimedResourcesCardSkeleton />
      ) : hasError ? (
        <FailedToLoad
          title="Failed to load reclaimed resources"
          refresh={refetch}
        />
      ) : (
        <Stack height="100%" gap={24} justifyContent="space-between">
          <Stack
            gap={24}
            data-testid="reclaimed-resources-card-summary"
            sx={{ flexShrink: 0 }}
          >
            <ResourceRow
              data={data?.cpu}
              unit="CPU"
              testId="reclaimed-resources-cpu"
            />
            <Divider />
          </Stack>
          <ResourceRow
            data={data?.memory}
            unit="GiB"
            testId="reclaimed-resources-memory"
          />
        </Stack>
      )}
    </WidgetLayout>
  );
};
